import { ComponentColor } from "../../../helpers/constants/types";
import { SchedulerLesson } from "../../../models/schedule/lesson";
import { SubstituteSchedulerLesson } from "../../scheduler/SubstituteScheduler";
import { IContent } from "../content-factory/IContent";
import {
  ISchedulerViewMode,
  SchedulerViewConfiguration,
} from "./ISchedulerViewMode";

export class HighlightTeacher<
  T extends SchedulerLesson | SubstituteSchedulerLesson,
> implements ISchedulerViewMode<T>
{
  constructor(
    private viewMode: ISchedulerViewMode<T>,
    private highlightTeacherId: string,
    private absentTeacherId?: string,
  ) {}

  toggleActive(day: number, duration: number, componentSchedule: T[][]): T[] {
    return this.viewMode.toggleActive(day, duration, componentSchedule);
  }

  setDay(day: number, value: boolean, componentSchedule: T[][]): T[] {
    return this.viewMode.setDay(day, value, componentSchedule);
  }

  displayLessons(
    startDate: Date,
    componentSchedule: T[][],
    page?: number,
  ): T[][] {
    return this.viewMode.displayLessons(startDate, componentSchedule, page);
  }

  getWeekday(
    startDate: Date,
    day: number,
  ): { id: number; name: string; shortName: string } {
    return this.viewMode.getWeekday(startDate, day);
  }

  hasPage(page: number, componentSchedule: T[][]): boolean {
    return this.viewMode.hasPage(page, componentSchedule);
  }

  getLessonComponentColor(lesson: T): ComponentColor {
    return this.viewMode.getLessonComponentColor(lesson);
  }

  getLessonComponentStyle(lesson: T): string {
    let componentStyle = this.viewMode.getLessonComponentStyle(lesson);

    const absentIsHighlighted =
      this.highlightTeacherId === this.absentTeacherId;
    const lessonIsUnassigned = this.isLessonUnassigned(lesson);
    const lessonIsAssignedTo = this.isLessonAssignedTo(
      lesson,
      this.highlightTeacherId,
    );
    if ((absentIsHighlighted && lessonIsUnassigned) || lessonIsAssignedTo) {
      componentStyle += " opacity-100";
    } else {
      componentStyle += " opacity-30";
    }
    return `${componentStyle} transition-opacity duration-300 ease-in-out`;
  }

  getLessonContent(
    lesson: T,
    viewConfiguration: SchedulerViewConfiguration,
  ): IContent | null {
    return this.viewMode.getLessonContent(lesson, viewConfiguration);
  }

  private isLessonUnassigned(lesson: T): boolean {
    return (
      !lesson.teacherAccepted &&
      (!lesson.teacherApplications || !lesson.teacherApplications.length) &&
      (!this.isSubstituteSchedulerLesson(lesson) || !lesson.selectedSubstitute)
    );
  }

  private isLessonAssignedTo(lesson: T, teacherId: string): boolean {
    return (
      lesson.teacherAccepted?.id === teacherId ||
      lesson.teacherApplications?.some((t) => t.id === teacherId) ||
      (this.isSubstituteSchedulerLesson(lesson) &&
        lesson.selectedSubstitute?.id === teacherId)
    );
  }

  private isSubstituteSchedulerLesson(
    lesson: SchedulerLesson | SubstituteSchedulerLesson,
  ): lesson is SubstituteSchedulerLesson {
    const selectedSubstituteKey: keyof SubstituteSchedulerLesson =
      "selectedSubstitute";
    return selectedSubstituteKey in lesson;
  }
}
